/** @file Single-line text input that commits on Enter or blur and cancels on Escape. */
import { useRef, useState, type KeyboardEvent } from 'react';
import './tokens.css';
import './TextInput.css';
import { joinClass } from './logic/joinClass';
import { textKeyAction } from './logic/textKeyAction';

/** Props of TextInput. */
export interface TextInputProps {
  value: string;
  /** Already-translated accessible name (the input shows no visible label of its own). */
  label: string;
  /** Called once with the new text when it differs from `value`. */
  onCommit: (next: string) => void;
  /** Called when editing ends without a change: Escape, or Enter/blur with the text unchanged. */
  onCancel?: (() => void) | undefined;
  placeholder?: string;
  disabled?: boolean;
  /** Focus and select the text on mount (rename mode). */
  autoFocus?: boolean;
  className?: string;
}

/** Text field with a local draft: `value` stays the source of truth until the user commits. */
export function TextInput({ value, label, onCommit, onCancel, placeholder, disabled = false, autoFocus = false, className }: TextInputProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const settled = useRef(false);
  const finish = (commit: boolean): void => {
    if (settled.current) return;
    settled.current = true;
    setDraft(null);
    if (commit && draft !== null && draft !== value) onCommit(draft);
    else onCancel?.();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    const action = textKeyAction(e.code, e.nativeEvent.isComposing, draft !== null, onCancel !== undefined);
    if (action === 'pass') return;
    e.preventDefault();
    e.stopPropagation();
    finish(action === 'commit');
  };
  return (
    <input
      type="text"
      className={joinClass('p2-text-input', className)}
      aria-label={label}
      value={draft ?? value}
      placeholder={placeholder}
      disabled={disabled}
      autoFocus={autoFocus}
      onFocus={(e) => { settled.current = false; e.currentTarget.select(); }}
      onChange={(e) => { settled.current = false; setDraft(e.currentTarget.value); }}
      onBlur={() => { finish(true); }}
      onKeyDown={onKeyDown}
    />
  );
}
